import {
  SUPPORTED_ACTION_PROPERTIES,
  isCustomPropertyAllowed,
} from '@packages/systems/ix3/schema';
import {IX3_WF_EXTENSION_KEYS} from '@packages/systems/ix3-wf-plugins/shared';

/**
 * Names the Designer once registered under a namespace and has since moved
 * elsewhere. Saved interactions can still carry them, so they stay allowed.
 * `display` and `class` sat in the style category before #94981 moved them to
 * the transform and class categories.
 */
const LEGACY_ACTION_PROPERTIES: Readonly<Record<string, readonly string[]>> = {
  [IX3_WF_EXTENSION_KEYS.STYLE]: ['display', 'class'],
};

/**
 * The style and transform runtime handlers hand every property name straight
 * to GSAP, which can write a name it does not recognize as a CSS property to
 * the element itself. The Designer only authors the names below, but the save
 * schema accepts any string, so the server enforces the same list.
 *
 * A Map, not an object: extension keys are author-controlled, and a plain
 * object would answer `constructor` or `__proto__` with an inherited value.
 */
const ACTION_PROPERTY_ALLOWLIST: ReadonlyMap<
  string,
  ReadonlySet<string>
> = new Map(
  [IX3_WF_EXTENSION_KEYS.STYLE, IX3_WF_EXTENSION_KEYS.TRANSFORM].map((key) => [
    key,
    new Set([
      ...(SUPPORTED_ACTION_PROPERTIES[key] ?? []),
      ...(LEGACY_ACTION_PROPERTIES[key] ?? []),
    ]),
  ])
);

/**
 * `wf:custom-property` has its own name rules, which the transform engine also
 * applies as an action transform. Repeating them here keeps them in force when
 * an earlier transform on the same action throws and the engine skips them.
 */
const CUSTOM_PROPERTY_KEY = IX3_WF_EXTENSION_KEYS.CUSTOM_PROPERTY;

/**
 * `wf:variable` nests its names one level down, under `variable`, and the
 * runtime spreads them into the tween. Once transformed they are CSS custom
 * property names, so anything else is not a variable.
 */
const VARIABLE_KEY = IX3_WF_EXTENSION_KEYS.VARIABLE;

function isPlainBag(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Whether this action namespace has an allowlist, so its value must be a
 * plain object of names.
 */
export function isGatedActionPropertyNamespace(extensionKey: string): boolean {
  return (
    ACTION_PROPERTY_ALLOWLIST.has(extensionKey) ||
    extensionKey === CUSTOM_PROPERTY_KEY
  );
}

/**
 * Whether a property name may be saved or published under this action
 * namespace. Namespaces without an allowlist here are not gated.
 */
export function isAllowedActionProperty(
  extensionKey: string,
  propertyName: string
): boolean {
  if (extensionKey === CUSTOM_PROPERTY_KEY) {
    return isCustomPropertyAllowed(propertyName);
  }
  const allowed = ACTION_PROPERTY_ALLOWLIST.get(extensionKey);
  return !allowed || allowed.has(propertyName);
}

/**
 * Returns a copy of one namespace's properties without the names that
 * namespace does not allow.
 */
export function filterAllowedActionProperties<T>(
  extensionKey: string,
  properties: Record<string, T>
): Record<string, T> {
  const filtered: Record<string, T> = {};
  for (const name of Object.keys(properties)) {
    if (isAllowedActionProperty(extensionKey, name)) {
      filtered[name] = properties[name] as T;
    }
  }
  return filtered;
}

type ActionWithProperties = {properties?: unknown};
type TimelineWithActions = {actions?: readonly ActionWithProperties[]};

/**
 * `enforce` publishes the filtered bags. `shadow` publishes the original
 * actions untouched and only reports what `enforce` would have dropped, so a
 * rollout can measure legacy data before it starts removing anything.
 */
export type ActionPropertyAllowlistMode = 'enforce' | 'shadow';

export type DroppedActionProperty = {
  extensionKey: string;
  /** `null` when the whole bag was dropped for not being a plain object. */
  name: string | null;
};

export type ActionPropertyFilterOptions = {
  mode: ActionPropertyAllowlistMode;
  /** Called once per call, and only when something would be dropped. */
  onDropped?: (dropped: readonly DroppedActionProperty[]) => void;
};

function filterVariableBag(
  bag: unknown,
  dropped: DroppedActionProperty[]
): unknown {
  if (!isPlainBag(bag) || !('variable' in bag)) {
    // Nothing for the runtime to spread: the transform returns early on a bag
    // with no `variables`, and the runtime ignores one with no `variable`.
    return bag;
  }
  if (!isPlainBag(bag.variable)) {
    dropped.push({extensionKey: VARIABLE_KEY, name: null});
    return {};
  }
  const variable: Record<string, unknown> = Object.create(null);
  for (const name of Object.keys(bag.variable)) {
    if (name.startsWith('--')) {
      variable[name] = bag.variable[name];
    } else {
      dropped.push({extensionKey: VARIABLE_KEY, name});
    }
  }
  return {...bag, variable};
}

function filterActionPropertyBags<A extends ActionWithProperties>(
  action: A,
  dropped: DroppedActionProperty[]
): A {
  const {properties} = action;
  if (properties == null || typeof properties !== 'object') {
    return action;
  }
  const bags = properties as Record<string, unknown>;
  // Null prototype so a copied `__proto__` key stays a plain key.
  const next: Record<string, unknown> = Object.create(null);
  for (const extensionKey of Object.keys(bags)) {
    const bag = bags[extensionKey];
    if (extensionKey === VARIABLE_KEY) {
      next[extensionKey] = filterVariableBag(bag, dropped);
    } else if (!isGatedActionPropertyNamespace(extensionKey)) {
      next[extensionKey] = bag;
    } else if (isPlainBag(bag)) {
      const record = bag as Record<string, unknown>;
      for (const name of Object.keys(record)) {
        if (!isAllowedActionProperty(extensionKey, name)) {
          dropped.push({extensionKey, name});
        }
      }
      next[extensionKey] = filterAllowedActionProperties(extensionKey, record);
    } else {
      // Dropped: the runtime iterates the bag with `for...in`, so a string or
      // array would hand its indices to GSAP as names.
      dropped.push({extensionKey, name: null});
    }
  }
  return {...action, properties: next};
}

/**
 * Publish-time pass over transformed timelines. It runs after the transform
 * engine rather than as one of its action transforms because the engine wraps
 * each action in a single try/catch: a throw from any earlier transform on the
 * action would skip a filter registered there and still publish the action.
 * Returns new objects; the engine's output is frozen. Options default to
 * `enforce`; the flagged publish paths always pass a mode explicitly.
 */
export function filterAllowedTimelineProperties<T extends TimelineWithActions>(
  timelines: readonly T[],
  {mode, onDropped}: ActionPropertyFilterOptions = {mode: 'enforce'}
): T[] {
  const dropped: DroppedActionProperty[] = [];
  const filtered = timelines.map((timeline) =>
    Array.isArray(timeline.actions)
      ? {
          ...timeline,
          actions: timeline.actions.map((action) =>
            filterActionPropertyBags(action, dropped)
          ),
        }
      : timeline
  );
  if (dropped.length > 0) {
    onDropped?.(dropped);
  }
  return mode === 'enforce' ? filtered : [...timelines];
}
